import React, { useState, useCallback } from "react";
import { Box, Grid, Paper } from "@mui/material";
import { mobiliTheme } from "../../../../../themes/mobiliTheme";
import SettingsSidebar from "./SettingsSidebar";
import SettingsContent from "./SettingsContent";

interface SettingsLayoutProps {
  children?: React.ReactNode;
}

export type SettingSection =
  | "TSS"
  | "Client"
  | "Transactions"
  | "Export"
  | "Dashboard";

interface SettingsContextType {
  activeSection: SettingSection;
  setActiveSection: (section: SettingSection) => void;
}

export const SettingsContext = React.createContext<SettingsContextType>({
  activeSection: "Dashboard",
  setActiveSection: () => {},
});

const SettingsLayout: React.FC<SettingsLayoutProps> = ({ children }) => {
  const [activeSection, setActiveSection] =
    useState<SettingSection>("Dashboard");

  const handleSectionChange = useCallback((section: SettingSection) => {
    setActiveSection(section);
  }, []);

  const contextValue = React.useMemo(
    () => ({
      activeSection,
      setActiveSection: handleSectionChange,
    }),
    [activeSection, handleSectionChange]
  );

  return (
    <SettingsContext.Provider value={contextValue}>
      <Box sx={{ height: "100vh", overflow: "hidden" }}>
        <Grid container sx={{ height: "100%" }}>
          {/* Left Sidebar - Settings Menu */}
          <Grid
            item
            xs={12}
            sm={1.5}
            md={1.5}
            sx={{
              borderRight: `1px solid ${mobiliTheme.palette.divider}`,
              backgroundColor: mobiliTheme.palette.grey[50],
              overflow: "auto",
            }}
          >
            <SettingsSidebar />
          </Grid>

          {/* Right Content Area */}
          <Grid
            item
            xs={12}
            sm={10.5}
            md={10.5}
            sx={{
              overflow: "auto",
              backgroundColor: mobiliTheme.palette.grey[100],
            }}
          >
            <Box sx={{ p: 3, height: "100%" }}>
              <SettingsContent />
              {children}
            </Box>
          </Grid>
        </Grid>
      </Box>
    </SettingsContext.Provider>
  );
};

export default SettingsLayout;
